/**
 * 앱 활성/백그라운드 전환 결정 — 순수 로직 (Phaser/Capacitor 무의존).
 *
 * 안드로이드 홈 이동(백그라운드) 시 사운드 음소거 + 게임 루프 정지, 복귀 시 해제/재개를 결정한다.
 * 입력 신호는 두 경로:
 *  - 네이티브/웹 공통: Capacitor `App.addListener('appStateChange', {isActive})` → {@link activityFromIsActive}
 *  - 폴백(웹 전용): `document.visibilityState` → {@link activityFromVisibility}
 * 결정만 순수 함수로 분리해 단위테스트로 100% 커버하고, 실제 mute/loop 제어는 GameScene이 수행한다.
 */

export type AppActivity = "active" | "background";

/** document.visibilityState → 활성도. `"hidden"`만 background, 그 외(visible/prerender)는 active(들리는 쪽이 안전). */
export function activityFromVisibility(state: string): AppActivity {
  return state === "hidden" ? "background" : "active";
}

/** Capacitor appStateChange의 isActive → 활성도 (네이티브 onPause/onResume에서 발생). */
export function activityFromIsActive(isActive: boolean): AppActivity {
  return isActive ? "active" : "background";
}

/** 전환 결정 결과 — `actions`는 현재 활성도에 맞는 mute/pause 값(재적용해도 무해), `changed`는 이전 대비 변화 여부. */
export interface LifecycleTransition {
  actions: { mute: boolean; pause: boolean };
  changed: boolean;
}

/**
 * 다음/이전 활성도로 음소거·일시정지 액션과 변화 여부를 결정한다.
 * `changed=false`(동일 상태 반복)면 호출자가 중복 mute/loop 토글을 건너뛸 수 있다(멱등 가드).
 * background면 {mute:true, pause:true}, active면 {mute:false, pause:false} — 사운드 정지는 mute가 단독 책임
 * (어떤 게임 루프 pause도 WebAudio를 멈추지 않으므로 두 액션을 직교로 반환).
 */
export function decideLifecycleTransition(
  next: AppActivity,
  prev: AppActivity,
): LifecycleTransition {
  const background = next === "background";
  return {
    actions: { mute: background, pause: background },
    changed: next !== prev,
  };
}
